import {
  IActivityNode,
  IAssessmentData,
  IAssessmentNode,
  IAttachment,
  IAttachmentsListNode,
  ICourseData,
  ICourseNode,
  IDocumentNode,
  IDuration,
  IInlineNode,
  ILessonPlan,
  INodeList,
  INoteData,
  INoteNode,
  IQuestionNode,
  ISimplifiedNode,
  ISkillsList,
  IUnitNode,
} from '../components/ingestion/IngestedTree'
import {
  AssignmentType,
  GradingType,
  NodeType,
  QuestionType,
} from 'shared/types/ingestion'
import mime from 'mime'
import {
  IClassifyingItem,
  IClassifyingTarget,
  IQuestionClassifying,
  IQuestionClassifyingData,
  IQuestionDataBase,
  IQuestionMultiChoiceResponse,
  IQuestionMultiSelectResponse,
  IQuestionShortAnswer,
  IQuestionShortResponse,
} from '../components/ingestion/IngestionQuestions'
import { PARAGRAPH_SPACING, wrapB, wrapP } from './html_utility'
import {
  genericQuestionType,
  INodeWithNodes,
  MultiAnswerProps,
  ShortAnswerProps,
} from './types'
import { parseHtmlString as parseHtmlStringUtil } from 'shared/utils'

export function convertParagraphSpacing(content?: string) {
  if (!content) return ''
  return content.replaceAll('\n\n', PARAGRAPH_SPACING)
}

export function parseHtmlString(htmlString?: string) {
  return parseHtmlStringUtil(convertParagraphSpacing(htmlString))
}

export function createCourse(
  data: ICourseData,
  children: ISimplifiedNode[] = [],
): ICourseNode {
  return {
    type: NodeType.Course,
    data,
    nodes: children,
  }
}

export function createUnit(
  title: string,
  children: ISimplifiedNode[] = [],
): IUnitNode {
  return {
    type: NodeType.Unit,
    data: { title },
    nodes: children,
  }
}

export function createNoteNode({ value, ...rest }: INoteData): INoteNode {
  return {
    type: NodeType.Note,
    data: {
      ...rest,
      value: value.replaceAll('\n\n', PARAGRAPH_SPACING),
    },
  }
}

export function createSkillsList(
  title: string,
  skillIds: string[],
): ISkillsList {
  return {
    type: NodeType.SkillsList,
    data: {
      title,
      skills: skillIds,
    },
  }
}

export function createDocumentNode<T>(
  title: string,
  nodes: T[],
): IDocumentNode<T> {
  return {
    type: NodeType.Document,
    data: { title },
    nodes,
  }
}

export function createMultiPartQuestion(
  questionText: string,
  questions: (IQuestionNode | INoteNode)[],
): IInlineNode {
  return {
    type: NodeType.Inline,
    data: {
      title: 'Multipart Question',
      studentFacing: true,
      type: NodeType.MultiPartQuestion,
    },
    nodes: [
      createNoteNode({
        title: 'Description',
        value: questionText,
        studentFacing: true,
      }),
      ...questions,
    ],
  }
}

export function addChildNode({
  parent,
  child,
}: {
  parent: INodeWithNodes
  child: ISimplifiedNode
}) {
  if (!parent.nodes) {
    parent.nodes = []
  }
  parent.nodes.push(child)
}

export function createLongCardListType({
  nodes,
  title,
}: {
  nodes: ISimplifiedNode[]
  title: string
}): INodeList {
  return {
    type: NodeType.NodeList,
    data: {
      title,
    },
    nodes,
  }
}

export function createLessonPlan(
  data: {
    title: string
    duration?: IDuration
  },
  nodes: ISimplifiedNode[],
): ILessonPlan {
  return {
    type: NodeType.LessonPlan,
    data,
    nodes,
  }
}

export function createAttachmentsList(data: {
  title: string
  studentFacing?: boolean
  ExternalID?: string
  attachments_list: IAttachment[]
}): IAttachmentsListNode {
  return {
    type: NodeType.AttachmentsList,
    data: {
      studentFacing: false,
      ...data,
    },
  }
}

export function createAttachment({
  name,
  url,
}: {
  name: string
  url: string
}): IAttachment {
  return {
    name,
    url,
    type: mime.getType(url) ?? undefined,
  }
}

export function createNodeList<T = ISimplifiedNode>(
  data: { title: string },
  nodes: T[],
): INodeList<T> {
  return {
    type: NodeType.NodeList,
    data,
    nodes,
  }
}

export function createAssessment(
  data: IAssessmentData,
  nodes: ISimplifiedNode[] = [],
): IAssessmentNode {
  return {
    type: NodeType.Assessment,
    data: {
      isAssignable: true,
      ...data,
    },
    nodes,
  }
}

export function createActivity(
  {
    title,
    isAssignable,
    assignment,
    assessment_grading,
    duration,
    points,
  }: {
    title: string
    isAssignable?: boolean
    points?: number
    assignment?: AssignmentType
    assessment_grading?: GradingType
    duration?: IDuration
  },
  nodes: ISimplifiedNode[],
): IActivityNode {
  return {
    type: NodeType.Activity,
    data: {
      title,
      duration,
      isAssignable: isAssignable === undefined ? true : isAssignable,
      assignment: assignment || 'participation',
      assessment_grading: assessment_grading || 'formative',
      points: points || 1,
    },
    nodes,
  }
}

export function addUnitsToCourse(
  course: ICourseNode,
  units: IUnitNode[],
  title: string = 'Units',
) {
  const nodeList: INodeList = {
    type: NodeType.NodeList,
    data: { title },
    nodes: units,
  }
  addChildNode({
    parent: course,
    child: nodeList,
  })
}

function formatQuestionData<T>({
  description,
  teacherNote,
  ...rest
}: Partial<IQuestionDataBase> & { description: string } & T) {
  const toReplace = [
    ['\n\n', PARAGRAPH_SPACING],
    ['\n', ''],
    ['\t', ''],
  ]
  return {
    points: 4,
    ...rest,
    description: toReplace.reduce(
      (acc, [r, w]) => acc.replaceAll(r, w),
      description,
    ),
    teacherNote: toReplace.reduce(
      (acc, [r, w]) => acc.replaceAll(r, w),
      teacherNote || '',
    ),
  }
}

export function createShortResponse(
  data: Partial<IQuestionDataBase> & { description: string },
): IQuestionShortResponse {
  return {
    type: NodeType.Question,
    data: {
      title: data.title || 'Question',
      studentFacing: true,
      ...formatQuestionData(data),
      type: QuestionType.ShortResponse,
    },
  }
}

export function createShortAnswer(
  data: ShortAnswerProps,
): IQuestionShortAnswer {
  return {
    type: NodeType.Question,
    data: {
      title: 'Question',
      studentFacing: true,
      ...formatQuestionData(data),
      type: QuestionType.ShortAnswer,
    },
  }
}

export type matchingQuestionData = {
  description: string
  targets: IClassifyingTarget[]
  items: IClassifyingItem[]
  allowAnswerReuse?: boolean
  teacherNote?: string
}

export function createMultiAnswerQuestion(
  qData: MultiAnswerProps,
): IQuestionMultiSelectResponse | IQuestionMultiChoiceResponse {
  const numberCorrect = qData.answers.filter((opt) => opt.correct).length
  const type =
    (qData.overrides?.type ?? numberCorrect > 1)
      ? QuestionType.MultipleSelect
      : QuestionType.MultipleChoice
  // Strange, for some reason this is required to make TS happy, probably better way?
  if (type === QuestionType.MultipleSelect) {
    return {
      type: NodeType.Question,
      data: {
        ...formatQuestionData(qData),
        type,
      },
    }
  }
  return {
    type: NodeType.Question,
    data: {
      ...formatQuestionData(qData),
      type,
    },
  }
}

export function createClassificationQuestion({
  title,
  description,
  targets,
  items,
  allowAnswerReuse,
}: {
  description: string
  targets: IClassifyingTarget[]
  items: IClassifyingItem[]
  title?: string
  allowAnswerReuse?: boolean
}): IQuestionClassifying {
  const questionData: genericQuestionType<IQuestionClassifyingData> = {
    description,
    title: title || 'Question',
    targets,
    items,
    allowAnswerReuse,
    studentFacing: true,
    type: QuestionType.Classifying,
  }
  return {
    type: NodeType.Question,
    data: questionData,
  }
}

export function wrapMathIfNeeded(content: string) {
  if (content.includes('\\') && !content.startsWith('\\(')) {
    return `\\(${content}\\)`
  }
  return content
}
export function cleanUpAnswerOptions(answers: string[]) {
  return answers.map((a) => {
    const clean = a
      .replace('&gt;', '>')
      .replace('&lt;', '<')
      .replace('\\%', '%')
    return wrapMathIfNeeded(clean)
  })
}

export function paragraphWithHeader(header: string, content: string) {
  return `${wrapP(wrapB(header))}${wrapP(content)}`
}

export function conditionalParagraphWithHeader(
  header: string,
  content?: string,
) {
  if (!content) return ''
  return paragraphWithHeader(header, content)
}
